import { useState, useEffect } from "react";
import {
    Box, Typography, Divider, Button, CircularProgress,
    TextField, FormControl, InputLabel, Select, MenuItem,
    RadioGroup, FormControlLabel, Radio, FormLabel,
    Card, CardContent, Container, Grid, Paper,
    Fade, Alert
} from "@mui/material";
import { CloudUpload, Link, Assessment } from "@mui/icons-material";

import { useUserContext } from "../../../contexts/ProviderProvider";
import useNotification from "../../../hooks/useNotification";
import { uploadGeneralEQMAssetByURL, uploadGeneralEQMAssetByFile } from "../../../API/equipmentManager";
import AssetTagManager from "./AssetTagManager";
import AssetInputer from "./AssetInputer";
import SingleSchoolSelector from "../../common/SingleSchoolSelector";

const ICON_TYPES_REQUIRING_UNIVERSITY = [
    "Front Asset Icon File",
    "Back Asset Icon File",
    "Jersey Art File",
    "Mockup Template File"
];

const ASSET_TYPES = [
    { value: "Font File", label: "Font File" },
    { value: "Mockup Template File", label: "Mockup Template File" },
    { value: "Front Asset Icon File", label: "Front Asset File" },
    { value: "Back Asset Icon File", label: "Back Asset Icon File" },
    { value: "Jersey Art File", label: "Jersey Art File" },
    { value: "Composite Font File", label: "Composite Font File" }
];

const AssetUploader = () => {
    const user = useUserContext();
    const notify = useNotification();

    const [uploadMethod, setUploadMethod] = useState("url");
    const [assetType, setAssetType] = useState("");
    const [name, setName] = useState("");
    const [tags, setTags] = useState([]);
    const [file, setFile] = useState(null);
    const [fileURL, setFileURL] = useState("");
    const [university, setUniversity] = useState("");
    const [uploading, setUploading] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});


    const validateForm = () => {
        const errors = {};

        if (!name.trim()) errors.name = "Name is required";
        if (!assetType) errors.assetType = "Asset type is required";
        if (tags.length === 0) errors.tags = "At least one tag is required";
        if (!uploadMethod) errors.uploadMethod = "Upload method is required";

        if (uploadMethod === "file" && !file) {
            errors.file = "Please select a file";
        }

        if (uploadMethod === "url" && !fileURL.trim()) {
            errors.fileURL = "Please provide a URL";
        }

        if (ICON_TYPES_REQUIRING_UNIVERSITY.includes(assetType) && !university.trim()) {
            errors.university = "University is required for this asset type";
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleUpload = async () => {
        if (!validateForm()) {
            notify({ msg: "Please fix the validation errors", variant: "warning" });
            return;
        }

        setUploading(true);
        try {
            if (uploadMethod === "file") {
                await uploadGeneralEQMAssetByFile(user, file, name, tags, assetType, university);
            } else {
                // remove part of string before first occurence of /Merch from fileURL
                const fileURLFormated = fileURL.replace(/^(.*\/Merch)/, "/Merch");
                console.log("fileURL", fileURLFormated);
                await uploadGeneralEQMAssetByURL(user, fileURLFormated, name, tags, assetType, university);
            }

            notify({ msg: "Upload successful!", variant: "success" });

            // Reset form
            setName("");
            setTags([]);
            setFile(null);
            setFileURL("");
            setAssetType("");
            setUniversity("");
            setValidationErrors({});
        } catch (err) {
            notify({ msg: "Upload failed.", variant: "error" });
            console.error(err);
        } finally {
            setUploading(false);
        }
    };

    const handleFileTypeChange = (value) => {
        setUploadMethod(value);
        setFile(null);
        setFileURL("");
        setValidationErrors({});
    };

    const handleInputChange = (field, value) => {
        // Clear validation error when user starts typing
        if (validationErrors[field]) {
            setValidationErrors(prev => ({ ...prev, [field]: undefined }));
        }

        switch (field) {
            case 'name':
                setName(value);
                break;
            case 'assetType':
                setAssetType(value);
                if (!ICON_TYPES_REQUIRING_UNIVERSITY.includes(value)) {
                    setUniversity("");
                }
                break;
            case 'university':
                setUniversity(value);
                break;
            case 'fileURL':
                setFileURL(value);
                break;
            default:
                break;
        }
    };

    const isFormValid = () => {
        return name && assetType && tags.length > 0 && uploadMethod &&
            (uploadMethod === "file" ? file : fileURL) &&
            (!ICON_TYPES_REQUIRING_UNIVERSITY.includes(assetType) || university);
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Upload Form */}
            <Card elevation={3} sx={{ mb: 4 }}>
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Assessment sx={{ mr: 2, color: 'primary.main' }} />
                        <Typography variant="h5" component="h1" fontWeight="600">
                            Upload Assets
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 4 }} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Upload Method Selection */}
                        <Paper elevation={1} sx={{ p: 3 }}>
                            <FormControl component="fieldset">
                                <FormLabel component="legend" sx={{ mb: 2, fontWeight: 500 }}>
                                    Upload Method
                                </FormLabel>
                                <RadioGroup
                                    row
                                    value={uploadMethod}
                                    onChange={(e) => handleFileTypeChange(e.target.value)}
                                >
                                    <FormControlLabel
                                        value="url"
                                        control={<Radio />}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Link sx={{ mr: 1, fontSize: 20 }} />
                                                Upload by URL
                                            </Box>
                                        }
                                    />
                                    <FormControlLabel
                                        value="file"
                                        control={<Radio />}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <CloudUpload sx={{ mr: 1, fontSize: 20 }} />
                                                Upload File
                                            </Box>
                                        }
                                    />
                                </RadioGroup>
                            </FormControl>
                            {validationErrors.uploadMethod && (
                                <Alert severity="error" sx={{ mt: 1 }}>
                                    {validationErrors.uploadMethod}
                                </Alert>
                            )}
                        </Paper>

                        {/* Asset Input Component */}
                        <Box>
                            <AssetInputer
                                method={uploadMethod}
                                file={file}
                                setFile={setFile}
                                fileURL={fileURL}
                                setFileURL={(value) => handleInputChange('fileURL', value)}
                                setName={(value) => handleInputChange('name', value)}
                                setTags={setTags}
                            />
                            {validationErrors.file && (
                                <Alert severity="error" sx={{ mt: 1 }}>
                                    {validationErrors.file}
                                </Alert>
                            )}
                            {validationErrors.fileURL && (
                                <Alert severity="error" sx={{ mt: 1 }}>
                                    {validationErrors.fileURL}
                                </Alert>
                            )}
                        </Box>

                        {/* Asset Type Selection */}
                        <FormControl
                            fullWidth
                            error={!!validationErrors.assetType}
                        >
                            <InputLabel>Asset Type</InputLabel>
                            <Select
                                value={assetType}
                                onChange={(e) => handleInputChange('assetType', e.target.value)}
                                label="Asset Type"
                            >
                                {ASSET_TYPES.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            {validationErrors.assetType && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                    {validationErrors.assetType}
                                </Typography>
                            )}
                        </FormControl>

                        {/* University Field (Conditional) */}
                        <Fade in={ICON_TYPES_REQUIRING_UNIVERSITY.includes(assetType)}>
                            <Box>
                                {ICON_TYPES_REQUIRING_UNIVERSITY.includes(assetType) && (
                                    <SingleSchoolSelector
                                        setSelectedSchool={setUniversity}
                                        selectedSchool={university}
                                        allButton={true}
                                    />
                                )}
                            </Box>
                        </Fade>

                        {/* Name Field */}
                        <TextField
                            label="Asset Name"
                            value={name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            fullWidth
                            error={!!validationErrors.name}
                            helperText={validationErrors.name}
                            required
                        />

                        {/* Tags Manager */}
                        <Box>
                            <AssetTagManager tags={tags} setTags={setTags} />
                            {validationErrors.tags && (
                                <Alert severity="error" sx={{ mt: 1 }}>
                                    {validationErrors.tags}
                                </Alert>
                            )}
                        </Box>

                        {/* Upload Button */}
                        <Button
                            variant="contained"
                            onClick={handleUpload}
                            disabled={uploading || !isFormValid()}
                            size="large"
                            sx={{
                                mt: 2,
                                py: 1.5,
                                position: 'relative',
                                '&:disabled': {
                                    opacity: 0.7
                                }
                            }}
                            startIcon={uploading ? null : <CloudUpload />}
                        >
                            {uploading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                    Uploading...
                                </Box>
                            ) : (
                                "Upload Asset"
                            )}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Container>
    );
};

export default AssetUploader;